import { computed, Directive, inject, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FileDocument } from '@lib/models/file-document.model';
import { EtatDer, EtatRecueil } from '@lib/models/generated/graphql';
import { AuthService } from '@lib/services/auth-service.service';
import { DocumentsService } from '@lib/services/documents.service';
import { OuiDialogService } from 'omnium-ui/dialog';
import { map } from 'rxjs';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { RecueilService } from '@lib/services/recueil.service';

@Directive()
export abstract class ConnaissanceClientDocumentBase {
  public investisseurService = inject(InvestisseurService);
  public dialogService = inject(OuiDialogService);
  public documentService = inject(DocumentsService);
  public recueilService = inject(RecueilService);
  public authService = inject(AuthService);
  protected readonly environment = inject<any>('environment' as any);
  readonly unsignedDocument = signal<FileDocument | null>(null);
  readonly signedDocument = signal<FileDocument | null>(null);
  readonly authorizedSignatures = signal<boolean>(this.environment.authoriseSignature);
  readonly isInvestisseurDead = this.investisseurService.isInvestisseurDead;
  readonly isAbonnementB2SValid = this.authService.isAbonnementB2SValid;
  readonly investisseur1Id = computed<number>(() => this.investisseurService.investisseurSig()?.id);
  abstract canSendToSignature: Signal<boolean>;
  protected abstract readonly documentName: string;
  readonly isDERNotReady = toSignal(
    this.recueilService.recueilState$.pipe(map(state => [EtatDer.ACompleter].includes(state?.derEtat as EtatDer))),
    {
      initialValue: false,
    }
  );
  readonly isRecueilNotReady = toSignal(
    this.recueilService.recueilState$.pipe(map(state => [EtatRecueil.ACompleter].includes(state?.recueilEtat as EtatRecueil))),
    {
      initialValue: false,
    }
  );
  readonly signatureTooltip = computed<string>(() => {
    if (this.authService.isBackOffice()) {
      return `Cette fonctionnalité n'est pas disponible pour les utilisateurs du back-office`;
    } else if (!this.authorizedSignatures()) {
      return 'Volontairement non disponible pour les tests';
    } else if (this.isInvestisseurDead()) {
      return 'Vous ne pouvez pas signer un document pour un investisseur décédé';
    } else if (!this.canSendToSignature()) {
      return 'Un document de ce type est déjà en cours de signature. Vous ne pouvez pas en envoyer un nouveau tant que la session actuelle n’est pas terminée.';
    } else if (this.signatureDisabledExtra() && this.isDERNotReady()) {
      return `L'envoi du ${this.documentName} en signature nécessite que le DER soit préalablement complété`;
    } else if (!this.isAbonnementB2SValid()) {
      return `Cette fonctionnalité nécessite un abonnement actif au Bouquet de Services`;
    } else if (this.signatureDisabledExtra() && this.isRecueilNotReady()) {
      return `L'envoi du ${this.documentName} en signature nécessite que le recueil soit préalablement complété`;
    }
    return '';
  });
  readonly signatureDisabled = computed<boolean>(() => {
    return (
      !this.authorizedSignatures() ||
      this.isInvestisseurDead() ||
      !this.canSendToSignature() ||
      this.signatureDisabledExtra() ||
      !this.isAbonnementB2SValid() ||
      this.authService.isBackOffice()
    );
  });
  protected signatureDisabledExtra(): boolean {
    return false;
  }
}
